import {Trans} from '@lingui/macro';
import React, {useEffect, useMemo, useState} from 'react';

import {useUnmountCancelToken} from '../../../../../srcCommon/helpers/hookHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {AnswerModel} from '../../../../models/api/answerModel';
import {getAnswersByNamespaces} from '../../../../store/data/answers/answersSelectors';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {getFlatTeammates, getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {getAvailableNamespaces} from '../../ruleHelpers';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {useRuleEditorServiceContext} from '../hooks/useRuleEditorServiceContext';
import {defaultDropdownWidth, GenericSingleParameterProps} from './genericParameter';

/*
 * Types.
 */

interface AnswerParameterProps extends GenericSingleParameterProps<AnswerModel> {
  namespace: string;
}

/*
 * Component.
 */

export const AnswerParameter = ({namespace, ...rest}: AnswerParameterProps) => {
  const answers = useAnswers(namespace);

  return (
    <SingleChoiceDropdown
      {...rest}
      items={answers}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => item.name}
      serializeItemForSearch={(item) => item.name}
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-select-answer">Select an answer</Trans>}
    />
  );
};

/*
 * Helpers.
 */

function useAnswers(namespace: string) {
  const [answers, setAnswers] = useState<ReadonlyArray<AnswerModel>>([]);

  const {answerService} = useRuleEditorServiceContext();
  const cancelToken = useUnmountCancelToken();
  const companyUrl = useAppSelector(getCompanyUrl);
  const user = useAppSelector(getUser);
  const answersByNamespaces = useAppSelector(getAnswersByNamespaces);
  const teammates = useAppSelector(getFlatTeammates);

  const namespaces = useMemo(
    () => getAvailableNamespaces(namespace, user, teammates),
    [namespace, teammates, user],
  );

  useEffect(() => {
    answerService
      .fetchByNamespacesWithCache({
        cancelToken,
        companyUrl,
        namespaces,
        answerMapCache: answersByNamespaces,
      })
      .then(setAnswers);
  }, [answerService, answersByNamespaces, cancelToken, companyUrl, namespaces]);

  return answers;
}
